import { Component, ReactNode } from 'react';
import { ThemeProvider, Theme } from '@mui/material/styles';
import { Subscription } from 'rxjs';
import { i18n } from './app-i18n';
import { appTheme, getRootFontSize } from './app.theme';
import { AppStore } from './app.store';
import { handleError } from 'common-services/handle-error';
import { getCurrentScriptURL } from 'common-services/paths';
import DxpBackdrop from 'common-dxp/backdrop/dxp-backdrop';
import Market from './market';
import AppWrapper from './app-wrapper';
import * as C from 'constants/constants';
import { createLexusTheme } from 'common-lexus/lexus.theme';
import { createDxpTheme } from 'common-dxp/dxp.theme';
import LexusBackdrop from 'common-lexus/backdrop/dxp-backdrop';
import { ThemeEnum } from 'types/market-types';
import { GlobalStyles } from 'tss-react';
import { getStyles, loadBrandFonts } from './app.tss';

interface Props {
  marketConfiguration: TimApp.MarketConfiguration;
  appSelector: string;
  theme?: ThemeEnum | null;
}

type StateType = {
  apiConfiguration: TimApp.ApiConfiguration | null;
  isErrorInJSON: boolean;
  marketConfiguration: TimApp.MarketConfiguration | null;
};

export class AppContainer extends Component<Props, StateType> {
  state: StateType = {
    apiConfiguration: null,
    isErrorInJSON: false,
    marketConfiguration: null,
  };

  subscription: Subscription = new Subscription();

  appTheme: Theme =
    this.props.marketConfiguration.brand === 'lexus'
      ? createLexusTheme(appTheme, getRootFontSize)
      : createDxpTheme(appTheme, getRootFontSize, this.props.theme as string);

  loadingComponent: ReactNode =
    this.props.marketConfiguration.brand === 'lexus' ? (
      <LexusBackdrop appSelector={this.props.appSelector} appLoading isLoading theme={appTheme} />
    ) : (
      <DxpBackdrop appSelector={this.props.appSelector} appLoading isLoading theme={appTheme} />
    );

  componentDidMount(): void {
    this.supportIE();
    this.setState((prevState) => ({
      ...prevState,
      marketConfiguration: this.props.marketConfiguration,
      theme: this.props.theme,
    }));
    i18n.on('missingKey', (lngs, namespace, key, res) => {
      handleError(
        new Error(`Missing key "${key}" in "${namespace}" namespace for ${lngs.join(', ')} languages. ${res}`)
      );
    });

    const appStore = new AppStore(this.getApiPath);
    appStore.init();

    this.subscription = appStore.observable.subscribe({
      next: (value) => {
        this.setState({ apiConfiguration: value });
      },
      complete: () => this.subscription.unsubscribe(),
    });

    loadBrandFonts(this.props.marketConfiguration.brand);

    this.areCorrectParamsFromJSON()
      .then((res) => {
        this.setState((prevstate) => ({ ...prevstate, isErrorInJSON: res }));
        return res;
      })
      .catch(handleError);
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.marketConfiguration !== this.props.marketConfiguration) {
      this.setState((prevState) => ({
        ...prevState,
        marketConfiguration: this.props.marketConfiguration,
        themeModel: this.props.theme,
      }));
    }
  }

  componentWillUnmount(): void {
    this.setState = (state, callback) => {};
    this.subscription.unsubscribe();
  }

  supportIE = (): void => {
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
    }
    if (!Element.prototype.closest) {
      Element.prototype.closest = function closest(s: string) {
        let that: (Node & ParentNode) | null;
        that = this;
        do {
          if (Element.prototype.matches.call(that, s)) return that;
          that = that.parentElement || that.parentNode;
        } while (that !== null && that.nodeType === 1);
        return null;
      };
    }
  };

  getApiPath: TimApp.AppPathFunc = (pathname: string) => `${getCurrentScriptURL()}/api${pathname}`;

  getPath: TimApp.AppPathFunc = (pathname: string) => `${getCurrentScriptURL()}${pathname}`;

  areCorrectParamsFromJSON = async (): Promise<boolean> => {
    const { country, elementType, brand = C.DEFAULT_BRAND_TYPE, vehicle } = this.props.marketConfiguration;

    if (!brand && vehicle?.brandName) {
      handleError(new Error(`Missing brand node in json config`));
      return true;
    }
    if (!country) {
      handleError(new Error(`Missing country node in json config`));
      return true;
    }
    if (!elementType) {
      handleError(new Error(`Missing elementType node in json config`));
      return true;
    }
    return false;
  };

  isEmptyApiConfigOrErrorOccur = (): boolean =>
    !this.state.apiConfiguration ||
    (this.state.apiConfiguration && !this.state.apiConfiguration.baseUrl && !this.state.apiConfiguration.error);

  render(): JSX.Element {
    if (this.state.isErrorInJSON) {
      return <></>;
    }
    return (
      <ThemeProvider theme={this.appTheme}>
        <GlobalStyles styles={getStyles(this.props.appSelector, this.appTheme)} />
        {this.isEmptyApiConfigOrErrorOccur() || !this.state.apiConfiguration ? (
          this.loadingComponent
        ) : (
          <AppWrapper flags={this.state.apiConfiguration?.flags}>
            {this.state.marketConfiguration && (
              <Market
                appSelector={this.props.appSelector}
                marketConfiguration={this.state.marketConfiguration}
                apiConfiguration={this.state.apiConfiguration}
                themeModel={this.props.theme as string}
              />
            )}
          </AppWrapper>
        )}
      </ThemeProvider>
    );
  }
}
